import {
  DateRange,
  DefaultMetricOptions,
  Goal,
  Task,
  Timestamp,
} from '@griply/types';
import { addDays, endOfDay, startOfDay } from 'date-fns';
import { useMemo } from 'react';

export type UseTimeframeTimesOptions = {
  allTasks: Task[];
  today: Timestamp;
};

export const useTimeframeTimes = (
  goal: Goal | undefined,
  { allTasks, today }: UseTimeframeTimesOptions,
): DateRange => {
  const firstCompletionDate = useMemo(() => {
    const completedTaskTimes = allTasks
      .filter(({ completedAt }) => !!completedAt)
      .map(({ completedAt }) => completedAt!.getTime());

    return completedTaskTimes.length &&
      goal &&
      (goal?.metric?.id === DefaultMetricOptions.CompletedTasks || !goal.metric)
      ? new Date(Math.min(...completedTaskTimes))
      : undefined;
  }, [allTasks, goal]);

  const lastCompletionDate = useMemo(
    () =>
      goal &&
      (goal?.metric?.id === DefaultMetricOptions.CompletedTasks || !goal.metric)
        ? new Date(
            Math.max(
              ...allTasks
                .filter(({ completedAt }) => !!completedAt)
                .map(({ completedAt }) => completedAt!.getTime()),
            ),
          )
        : undefined,
    [allTasks, goal],
  );

  const firstMetricEntryDate = useMemo(
    () =>
      goal?.metric?.entries?.length
        ? new Date(
            Math.min(...goal.metric.entries.map(({ date }) => date.getTime())),
          )
        : undefined,
    [goal?.metric?.entries],
  );
  const lastMetricEntryDate = useMemo(
    () =>
      goal?.metric?.entries?.length
        ? new Date(
            Math.max(...goal.metric.entries.map(({ date }) => date.getTime())),
          )
        : undefined,
    [goal?.metric?.entries],
  );

  const startDate = useMemo(
    () =>
      firstCompletionDate ||
      firstMetricEntryDate ||
      goal?.startDate ||
      goal?.deadline
        ? startOfDay(
            new Date(
              Math.min(
                ...([
                  firstCompletionDate?.getTime(),
                  firstMetricEntryDate?.getTime(),
                  goal?.deadline?.getTime(),
                  goal?.startDate?.getTime(),
                ].filter(Boolean) as number[]),
              ),
            ),
          )
        : addDays(today, -30),
    [
      firstCompletionDate,
      firstMetricEntryDate,
      goal?.deadline,
      goal?.startDate,
      today,
    ],
  );

  const endDate = useMemo(() => {
    const timestamps = [
      lastCompletionDate?.getTime(),
      lastMetricEntryDate?.getTime(),
      goal?.deadline?.getTime(),
      goal?.startDate ? addDays(goal.startDate, 30).getTime() : undefined,
      !goal?.completedAt ? addDays(today, goal?.deadline ? 0 : 30) : undefined,
    ].filter(Boolean) as number[];
    return endOfDay(
      new Date(timestamps.length ? Math.max(...timestamps) : today.getTime()),
    );
  }, [
    goal?.completedAt,
    goal?.deadline,
    goal?.startDate,
    lastCompletionDate,
    lastMetricEntryDate,
    today,
  ]);

  return useMemo(
    () => ({
      startDate,
      endDate,
    }),
    [endDate, startDate],
  );
};
